import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const DISMISS_OFFSET = 140;
const DISMISS_VELOCITY = 800;
const AXIS_LOCK_PX = 8;
const CLOSE_MS = 200;
const OPEN_MS = 200;
const SNAP_EASING = 'ease';

function isScrollableY(el) {
  if (!el || el === document.body || el === document.documentElement) return false;
  const style = window.getComputedStyle(el);
  const overflowY = style.overflowY;
  if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false;
  return el.scrollHeight > el.clientHeight + 1;
}

function getParentScrollableY(el, includeSelf = true) {
  let node = includeSelf ? el : el?.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    if (isScrollableY(node)) return node;
    node = node.parentElement;
  }
  return null;
}

function isAllScrollsOnTop(el, sheetEl) {
  let node = getParentScrollableY(el, true);
  while (node) {
    if (node === sheetEl || node === document.body || node === document.documentElement) break;
    if (node.scrollTop > 0) return false;
    node = getParentScrollableY(node, false);
  }
  return true;
}

function isSwipeBlocked(el) {
  return Boolean(el?.closest?.('[data-swipe-blocking="true"]'));
}

function isFromHandle(target) {
  return Boolean(target?.closest?.(
    '.mobile-menu-sheet__thumb, .mobile-menu-sheet__header, .mobile-account-sheet__thumb, .mobile-account-sheet__header, .mobile-account-sheet__subheader, .mobile-settings-sheet__thumb, .mobile-settings-sheet__header, .mobile-help-search-sheet__header, .cgp-m-sheet__thumb, .auth-code-help-sheet__thumb',
  ));
}

export function useBottomSheetGestures({
  open,
  onClose,
  dismissEnabled = true,
  handleOnly = false,
  scrollKey = 0,
}) {
  const sheetRef = useRef(null);
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const offsetRef = useRef(0);
  const [offsetY, setOffsetY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [closing, setClosing] = useState(false);
  const [entered, setEntered] = useState(false);
  const [opening, setOpening] = useState(false);

  const setOffset = useCallback((value) => {
    offsetRef.current = value;
    setOffsetY(value);
  }, []);

  useEffect(() => {
    if (!open) {
      setOffset(0);
      setDragging(false);
      setClosing(false);
      setEntered(false);
      setOpening(false);
      dragRef.current = null;
      return undefined;
    }

    setEntered(false);
    setOpening(true);
    setOffset(0);
    dragRef.current = null;

    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        setEntered(true);
      });
    });

    const done = window.setTimeout(() => setOpening(false), OPEN_MS + 32);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(done);
    };
  }, [open, setOffset]);

  const finishDrag = useCallback((velocityY = 0, cancelled = false) => {
    const drag = dragRef.current;
    const offset = offsetRef.current;
    const height = sheetRef.current?.offsetHeight || window.innerHeight || 800;
    const shouldDismiss = !cancelled
      && dismissEnabled
      && drag?.axis === 'y'
      && drag?.started
      && (offset >= DISMISS_OFFSET || offset > height * 0.5 || velocityY >= DISMISS_VELOCITY);

    if (shouldDismiss) {
      setClosing(true);
      setDragging(false);
      setOffset(height);
      window.setTimeout(() => {
        onClose();
        setOffset(0);
        setClosing(false);
      }, CLOSE_MS);
    } else {
      setDragging(false);
      setOffset(0);
    }
    dragRef.current = null;
  }, [dismissEnabled, onClose, setOffset]);

  const beginDrag = useCallback((target, screenX, screenY) => {
    if (!open || closing || opening || !dismissEnabled) return false;
    if (document.activeElement?.tagName?.toLowerCase() === 'input') return false;
    if (isSwipeBlocked(target)) return false;

    const fromHandle = isFromHandle(target);
    if (handleOnly && !fromHandle) return false;
    if (!fromHandle && !isAllScrollsOnTop(target, sheetRef.current)) return false;

    dragRef.current = {
      startX: screenX,
      startY: screenY,
      lastY: screenY,
      lastTime: Date.now(),
      velocityY: 0,
      fromHandle,
      started: false,
      axis: null,
      target,
    };
    return true;
  }, [open, closing, opening, dismissEnabled, handleOnly]);

  const moveDrag = useCallback((screenX, screenY, event) => {
    const drag = dragRef.current;
    if (!drag || drag.axis === 'x') return;

    const now = Date.now();
    const dt = (now - drag.lastTime) / 1000;
    if (dt > 0) {
      drag.velocityY = (screenY - drag.lastY) / dt;
    }
    drag.lastY = screenY;
    drag.lastTime = now;

    const dx = screenX - drag.startX;
    const dy = screenY - drag.startY;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (!drag.axis && (absX > AXIS_LOCK_PX || absY > AXIS_LOCK_PX)) {
      drag.axis = absX > absY ? 'x' : 'y';
      if (drag.axis === 'x') {
        dragRef.current = null;
        setDragging(false);
        setOffset(0);
        return;
      }
    }

    if (drag.axis !== 'y') return;

    if (dy <= 0) {
      if (drag.started) {
        setOffset(0);
        setDragging(false);
        drag.started = false;
      }
      return;
    }

    if (!drag.fromHandle && !isAllScrollsOnTop(drag.target, sheetRef.current)) {
      finishDrag(0, true);
      return;
    }

    drag.started = true;
    setDragging(true);
    setOffset(dy);
    if (event?.cancelable) event.preventDefault();
  }, [finishDrag, setOffset]);

  useEffect(() => {
    if (!open || !dismissEnabled || closing) return undefined;
    const sheetEl = sheetRef.current;
    if (!sheetEl) return undefined;

    const onTouchStart = (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      beginDrag(event.target, touch.screenX, touch.screenY);
    };

    const onTouchMove = (event) => {
      const touch = event.touches[0];
      if (!touch || !dragRef.current) return;
      moveDrag(touch.screenX, touch.screenY, event);
    };

    const onTouchEnd = () => {
      if (!dragRef.current) return;
      if (dragRef.current.axis === 'x') {
        dragRef.current = null;
        return;
      }
      finishDrag(dragRef.current.velocityY, false);
    };

    const onTouchCancel = () => {
      if (!dragRef.current) return;
      finishDrag(0, true);
    };

    sheetEl.addEventListener('touchstart', onTouchStart, { passive: true });
    sheetEl.addEventListener('touchmove', onTouchMove, { passive: false });
    sheetEl.addEventListener('touchend', onTouchEnd, { passive: true });
    sheetEl.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      sheetEl.removeEventListener('touchstart', onTouchStart);
      sheetEl.removeEventListener('touchmove', onTouchMove);
      sheetEl.removeEventListener('touchend', onTouchEnd);
      sheetEl.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [open, dismissEnabled, closing, beginDrag, moveDrag, finishDrag, scrollKey]);

  const onPointerDown = useCallback((event) => {
    if (event.pointerType === 'touch') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    beginDrag(event.target, event.screenX, event.screenY);
  }, [beginDrag]);

  const onPointerMove = useCallback((event) => {
    if (event.pointerType === 'touch') return;
    if (!dragRef.current) return;
    moveDrag(event.screenX, event.screenY, event);
  }, [moveDrag]);

  const onPointerUp = useCallback((event) => {
    if (event.pointerType === 'touch') return;
    if (!dragRef.current) return;
    if (dragRef.current.axis === 'x') {
      dragRef.current = null;
      return;
    }
    finishDrag(dragRef.current.velocityY, false);
  }, [finishDrag]);

  const height = typeof window !== 'undefined' ? window.innerHeight : 800;
  const dragProgress = Math.min(offsetY / (height * 0.72), 1);

  let translateY = height;
  if (closing) {
    translateY = offsetY > 0 ? offsetY : height;
  } else if (dragging || offsetY > 0) {
    translateY = offsetY;
  } else if (entered) {
    translateY = 0;
  }

  useLayoutEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    if (!dismissEnabled) {
      el.style.removeProperty('transform');
      el.style.removeProperty('transition');
      return;
    }
    const enableTransition = entered && !dragging;
    el.style.setProperty('transform', `translateY(${translateY}px)`, 'important');
    el.style.setProperty(
      'transition',
      enableTransition ? `transform ${closing ? CLOSE_MS : OPEN_MS}ms ${SNAP_EASING}` : 'none',
      'important',
    );
  }, [translateY, dragging, closing, entered, dismissEnabled]);

  const overlayStyle = (() => {
    if (!dismissEnabled) return undefined;
    if (closing) return { opacity: 0 };
    if (dragging || offsetY > 0) {
      return { opacity: Math.max(0.12, 1 - dragProgress * 0.88) };
    }
    if (!entered) return { opacity: 0 };
    return undefined;
  })();

  return {
    sheetRef,
    scrollRef,
    sheetStyle: undefined,
    overlayStyle,
    dragging,
    closing,
    opening,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
